// Pure layout for the "YOUR CIRCUIT" map (src/components/CircuitMap.jsx). No React, DOM or
// scenario imports, so it is unit-testable and accepts old saved scenario snapshots as data.
//
// Picture: a left-hand circuit (counter-clockwise on screen) around one schematic runway.
// The aircraft takes off at the RIGHT end heading right, climbs up the right leg, flies
// downwind leftwards along the top, descends the left leg, turns final and lands at the
// LEFT end heading right. Only the scenario's own stages get markers, and every badge,
// status and display label mirrors the practice sidebar stage list.

export const CIRCUIT_VIEWBOX = Object.freeze({ width: 100, height: 82 });
export const MARKER_RADIUS = 4.2;
/** The aircraft glyph is drawn at this scale; AIRCRAFT_RADIUS bounds it, white outline included. */
export const AIRCRAFT_SCALE = 0.58;
export const AIRCRAFT_RADIUS = 7.4;
/** Markers never come closer than this (centre to centre). */
export const MIN_MARKER_DISTANCE = 2 * MARKER_RADIUS + 1.5;
/** Top-down swept-wing aircraft, nose up, centred on 0,0; fits a circle of radius 12 (scale it down). */
export const AIRCRAFT_PATH = 'M0 -11C1.05 -11 1.6 -9.9 1.6 -8.6V-3.4L11 2.3V4.3L1.6 1.5L1.35 7.1L4.5 9.5V11L0 10.1L-4.5 11V9.5L-1.35 7.1L-1.6 1.5L-11 4.3V2.3L-1.6 -3.4V-8.6C-1.6 -9.9 -1.05 -11 0 -11Z';

const LOOP = { left: 10, right: 90, top: 11, bottom: 64, radius: 14 };
const RUNWAY = { left: 32, right: 68, y: LOOP.bottom, height: 6 };
const LANDING_STAGE_IDS = ['landing', 'visual_approach', 'brake_failure', 'flap_failure'];
const TITLE = { x: 50, centerY: (LOOP.top + LOOP.bottom) / 2, maxWidth: 54, sizes: [5.2, 4.6, 4.1], maxLines: 3, badgeRadius: 3.2, badgeGap: 2 };
const PATH_GAP = 12.5; // path distance; chord across the 14-unit corners still exceeds MIN_MARKER_DISTANCE
const TURN_STEP = 20; // how far the aircraft moves along the leg for each later turn of one stage
const TAG = { size: 4.4, below: MARKER_RADIUS + 6.6 };

const round = value => Math.round(value * 100) / 100;
const line = (x1, y1, x2, y2) => ({ kind: 'line', x1, y1, x2, y2, length: Math.hypot(x2 - x1, y2 - y1) });
// Arc angles are in degrees on screen axes (y down): point = centre + r·(cos θ, sin θ).
const arc = (cx, cy, r, from, to) => ({ kind: 'arc', cx, cy, r, from, to, length: r * Math.abs(to - from) * Math.PI / 180 });
const { left: L, right: R, top: T, bottom: B, radius: r } = LOOP;
const SEGMENTS = [
  line(RUNWAY.left, B, RUNWAY.right, B), // 0 takeoff roll, under the runway
  line(RUNWAY.right, B, R - r, B), // 1 climb-out
  arc(R - r, B - r, r, 90, 0), // 2 turn up the right leg
  line(R, B - r, R, T + r), // 3 departure, right leg, upwards
  arc(R - r, T + r, r, 0, -90), // 4 turn onto downwind
  line(R - r, T, L + r, T), // 5 downwind / waypoint, leftwards along the top
  arc(L + r, T + r, r, -90, -180), // 6 top-left turn (heading correction)
  line(L, T + r, L, B - r), // 7 visual approach, left leg, downwards
  arc(L + r, B - r, r, 180, 90), // 8 final turn (final approach, short final)
  line(L + r, B, RUNWAY.left, B), // 9 final, to the runway's left end
];
const STARTS = SEGMENTS.reduce((list, segment, index) => [...list, index ? list[index - 1] + SEGMENTS[index - 1].length : 0], []);
const START = STARTS[1]; // right end of the runway, where the circuit begins
const END = STARTS[9] + SEGMENTS[9].length; // left end of the runway, where it ends
const at = (index, fraction) => STARTS[index] + SEGMENTS[index].length * fraction;
/** Preferred path position (distance along the circuit) of each known stage. */
const SLOTS = {
  takeoff: START,
  departure: at(3, 0.5),
  downwind: at(5, 0.22),
  waypoint: at(5, 0.36),
  'heading-correction': at(6, 0.5),
  visual_approach: at(7, 0.5),
  brake_failure: STARTS[8] + 4,
  flap_failure: STARTS[8] + SEGMENTS[8].length - 5,
  landing: END,
};
// Older snapshots also carry circuitPosition ids; use them for any stage id this file does not know.
const POSITION_SLOTS = { ground: START, holding: START, takeoff: START, departure: SLOTS.departure, downwind: SLOTS.waypoint, base: SLOTS.visual_approach, final: END };

function pointAt(s) {
  const distance = Math.min(Math.max(s, 0), END);
  let index = SEGMENTS.length - 1;
  while (index > 0 && STARTS[index] > distance) index -= 1;
  const segment = SEGMENTS[index];
  const t = Math.min(Math.max(distance - STARTS[index], 0), segment.length);
  let x, y, dx, dy;
  if (segment.kind === 'line') {
    const f = segment.length ? t / segment.length : 0;
    x = segment.x1 + (segment.x2 - segment.x1) * f; y = segment.y1 + (segment.y2 - segment.y1) * f;
    dx = segment.x2 - segment.x1; dy = segment.y2 - segment.y1;
  } else {
    const sign = Math.sign(segment.to - segment.from);
    const theta = (segment.from + sign * (t / segment.r) * 180 / Math.PI) * Math.PI / 180;
    x = segment.cx + segment.r * Math.cos(theta); y = segment.cy + segment.r * Math.sin(theta);
    dx = -sign * Math.sin(theta); dy = sign * Math.cos(theta);
  }
  // 0° = nose up; positive turns clockwise, like SVG rotate().
  let rotation = Math.atan2(dx, -dy) * 180 / Math.PI;
  if (rotation <= -180) rotation += 360;
  return { x: round(x), y: round(y), rotation: round(rotation) + 0 };
}

function pathData(from, to) {
  const start = pointAt(from);
  const parts = [`M${start.x} ${start.y}`];
  SEGMENTS.forEach((segment, index) => {
    const a = Math.max(from, STARTS[index]), b = Math.min(to, STARTS[index] + segment.length);
    if (b <= a) return;
    const end = pointAt(b);
    parts.push(segment.kind === 'line' ? `L${end.x} ${end.y}` : `A${segment.r} ${segment.r} 0 0 ${segment.to > segment.from ? 1 : 0} ${end.x} ${end.y}`);
  });
  return parts.join(' ');
}

const displayLabel = stage => String(stage?.label ?? stage?.id ?? '').replace(/^Stage \d+ — /, '');

/** Badge, status and current flag exactly as the sidebar stage list derives them. */
export function stageBadges(scenario, cursor = {}, turnStatus = {}) {
  const stages = Array.isArray(scenario?.stages) ? scenario.stages : [];
  const current = Number.isInteger(cursor?.stageIndex) && stages[cursor.stageIndex] ? cursor.stageIndex : 0;
  return stages.map((stage, stageIndex) => {
    const turns = Array.isArray(stage?.turns) ? stage.turns : [];
    const statuses = turns.map(turn => turnStatus?.[`${stage.id}/${turn.id}`]);
    const cleared = statuses.every(value => value === 'cleared');
    const skipped = !cleared && statuses.includes('skipped');
    const number = stages.slice(0, stageIndex + 1).filter(item => !item?.optional).length;
    const badge = stage?.optionalKind === 'heading' ? 'H' : stage?.optionalKind === 'emergency' ? '!' : String(number);
    const kind = stage?.optionalKind === 'heading' ? 'heading' : stage?.optionalKind === 'emergency' ? 'emergency' : 'required';
    return { stageIndex, id: stage?.id ?? `stage-${stageIndex}`, label: displayLabel(stage), fullLabel: String(stage?.label ?? ''), badge, kind, optional: stage?.optional === true, status: cleared ? 'cleared' : skipped ? 'skipped' : 'pending', current: stageIndex === current };
  });
}

/** Path positions for every stage: fixed slots by id, interpolation for unknown ids, then spacing. */
function placeStages(stages) {
  const known = stages.map(stage => SLOTS[stage?.id] ?? POSITION_SLOTS[stage?.circuitPosition]);
  const preferred = known.map((value, index) => {
    if (value !== undefined) return value;
    let before = index - 1, after = index + 1;
    while (before >= 0 && known[before] === undefined) before -= 1;
    while (after < known.length && known[after] === undefined) after += 1;
    const low = before >= 0 ? known[before] : START, high = after < known.length ? known[after] : END;
    const span = after - before;
    return low + (Math.max(high, low) - low) * (index - before) / span;
  });
  const order = preferred.map((s, index) => ({ s, index })).sort((a, b) => a.s - b.s || a.index - b.index);
  for (let i = 1; i < order.length; i += 1) order[i].s = Math.max(order[i].s, order[i - 1].s + PATH_GAP);
  if (order.length && order.at(-1).s > END) {
    order.at(-1).s = END;
    for (let i = order.length - 2; i >= 0; i -= 1) order[i].s = Math.min(order[i].s, order[i + 1].s - PATH_GAP);
  }
  if (order.length > 1 && order[0].s < START) order.forEach((item, i) => { item.s = START + (END - START) * i / (order.length - 1); });
  const placed = [];
  order.forEach(item => { placed[item.index] = item.s; });
  return { placed, order };
}

// Conservative advance widths (em) close to a bold grotesque, so wrapped lines fit Figtree or a fallback font.
const CHAR_WIDTHS = [[' ijlI.,:;|!\'', 0.3], ['ft()[]r-/', 0.38], ['z', 0.52], ['abcdeghknopqsuvxy0123456789?$', 0.6], ['FJLTZ', 0.64], ['ABCDEHKNPRSUVXY&#', 0.74], ['GOQ', 0.8], ['mwM', 0.9], ['W—', 1]];
export function estimateTextWidth(text, fontSize) {
  let em = 0;
  for (const character of String(text)) em += (CHAR_WIDTHS.find(([set]) => set.includes(character))?.[1] ?? 0.64);
  return em * fontSize;
}

function wrapWords(text, fontSize, maxWidth) {
  const lines = [];
  for (const word of String(text).split(/\s+/).filter(Boolean)) {
    const candidate = lines.length ? `${lines.at(-1)} ${word}` : word;
    if (lines.length && estimateTextWidth(candidate, fontSize) <= maxWidth) lines[lines.length - 1] = candidate;
    else {
      let rest = word;
      while (estimateTextWidth(rest, fontSize) > maxWidth && rest.length > 1) {
        let cut = rest.length - 1;
        while (cut > 1 && estimateTextWidth(rest.slice(0, cut), fontSize) > maxWidth) cut -= 1;
        lines.push(rest.slice(0, cut)); rest = rest.slice(cut);
      }
      lines.push(rest);
    }
  }
  return lines;
}

function titleLayout(text, badge) {
  let size = TITLE.sizes[0], lines = wrapWords(text, size, TITLE.maxWidth);
  for (const next of TITLE.sizes.slice(1)) {
    if (lines.length <= TITLE.maxLines) break;
    size = next; lines = wrapWords(text, size, TITLE.maxWidth);
  }
  if (lines.length > TITLE.maxLines) {
    lines = lines.slice(0, TITLE.maxLines);
    let last = lines.at(-1);
    while (last.length > 1 && estimateTextWidth(`${last}…`, size) > TITLE.maxWidth) last = last.slice(0, -1);
    lines[lines.length - 1] = `${last.trimEnd()}…`;
  }
  const lineHeight = round(size * 1.2);
  const badgeHeight = badge ? TITLE.badgeRadius * 2 + TITLE.badgeGap : 0;
  const height = badgeHeight + lines.length * lineHeight;
  const top = TITLE.centerY - height / 2;
  const width = Math.max(0, ...lines.map(item => estimateTextWidth(item, size)));
  return {
    text, lines, x: TITLE.x, fontSize: size, lineHeight, firstBaseline: round(top + badgeHeight + lineHeight * 0.8),
    badge: badge ? { ...badge, x: TITLE.x, y: round(top + TITLE.badgeRadius), r: TITLE.badgeRadius } : null,
    box: { x: round(TITLE.x - width / 2), y: round(top), width: round(width), height: round(height) },
  };
}

function runwayTags(scenario, stages) {
  const ids = stages.map(stage => stage?.id);
  const takeoff = ids.includes('takeoff') && scenario?.takeoffRunway ? String(scenario.takeoffRunway) : null;
  const landingValue = scenario?.landingRunway ?? scenario?.runway;
  const landing = ids.some(id => LANDING_STAGE_IDS.includes(id)) && landingValue ? String(landingValue) : null;
  const y = round(RUNWAY.y + TAG.below + TAG.size * 0.72);
  if (takeoff && landing && takeoff === landing) return [{ kind: 'shared', text: takeoff, x: round((RUNWAY.left + RUNWAY.right) / 2), y, fontSize: TAG.size }];
  return [
    ...(landing ? [{ kind: 'landing', text: landing, x: RUNWAY.left, y, fontSize: TAG.size }] : []),
    ...(takeoff ? [{ kind: 'takeoff', text: takeoff, x: RUNWAY.right, y, fontSize: TAG.size }] : []),
  ];
}

/**
 * Everything the circuit map draws, in viewBox units.
 * @param scenario the active scenario (current, or an older saved snapshot)
 * @param cursor {stageIndex, turnIndex}; invalid values fall back to the first stage/turn like App does
 * @param turnStatus {`${stageId}/${turnId}`: 'cleared'|'skipped'}
 */
export function circuitLayout(scenario, cursor = {}, turnStatus = {}) {
  const stages = Array.isArray(scenario?.stages) ? scenario.stages : [];
  const badges = stageBadges(scenario, cursor, turnStatus);
  const { placed, order } = placeStages(stages);
  const markers = badges.map(item => { const { x, y } = pointAt(placed[item.stageIndex]); return { ...item, s: round(placed[item.stageIndex]), x, y }; });
  const currentMarker = markers.find(marker => marker.current) ?? null;
  let aircraft = null;
  if (currentMarker) {
    const stage = stages[currentMarker.stageIndex];
    const turnCount = Math.max(1, stage?.turns?.length ?? 1);
    const turnIndex = Number.isInteger(cursor?.turnIndex) && cursor.turnIndex >= 0 && cursor.turnIndex < turnCount ? cursor.turnIndex : 0;
    // Later turns of one stage move along the leg, never into the next marker.
    const rank = order.findIndex(item => item.index === currentMarker.stageIndex);
    const limit = (rank + 1 < order.length ? order[rank + 1].s - PATH_GAP : END) - placed[currentMarker.stageIndex];
    const step = turnCount > 1 ? Math.max(0, Math.min(TURN_STEP, limit / (turnCount - 1))) : 0;
    const s = placed[currentMarker.stageIndex] + step * turnIndex;
    // atMarker: the aircraft (mostly) covers its own stage marker, so the map paints only the aircraft there.
    aircraft = { stageIndex: currentMarker.stageIndex, turnIndex, s: round(s), atMarker: step * turnIndex < AIRCRAFT_RADIUS + MARKER_RADIUS, ...pointAt(s) };
  }
  const centre = (RUNWAY.left + RUNWAY.right) / 2;
  return {
    viewBox: `0 0 ${CIRCUIT_VIEWBOX.width} ${CIRCUIT_VIEWBOX.height}`, width: CIRCUIT_VIEWBOX.width, height: CIRCUIT_VIEWBOX.height,
    loop: { ...LOOP }, path: pathData(START, END),
    runway: { x: RUNWAY.left, y: round(RUNWAY.y - RUNWAY.height / 2), width: RUNWAY.right - RUNWAY.left, height: RUNWAY.height, centerX: centre, centerY: RUNWAY.y, left: RUNWAY.left, right: RUNWAY.right, centreline: { x1: round(RUNWAY.left + MARKER_RADIUS + 2), x2: round(RUNWAY.right - MARKER_RADIUS - 2), y: RUNWAY.y } },
    tags: runwayTags(scenario, stages),
    markers, aircraft,
    title: currentMarker ? titleLayout(currentMarker.label, { text: currentMarker.badge, kind: currentMarker.kind, status: currentMarker.status }) : null,
    current: currentMarker ? { stageIndex: currentMarker.stageIndex, label: currentMarker.label, fullLabel: currentMarker.fullLabel } : null,
  };
}
